import React from "react";
import { Loader2 } from "lucide-react";
import { Badge, type BadgeProps } from "./ui/badge";
import { cn } from "../lib/utils";

export interface IntegrationSelectionOption {
  id: string;
  name?: string | null;
  description?: string | null;
  [key: string]: unknown;
}

export interface IntegrationSelectionListProps {
  integrations: IntegrationSelectionOption[];
  selectedIntegrationIds: Iterable<string>;
  onToggle: (integrationId: string, checked: boolean) => void;
  loading?: boolean;
  emptyMessage?: React.ReactNode;
  pendingIntegrationId?: string | null;
  disabled?: boolean;
  formatIntegrationName?: (integration: IntegrationSelectionOption) => string;
  selectedBadgeLabel?: React.ReactNode;
  unselectedBadgeLabel?: React.ReactNode;
  selectedBadgeVariant?: BadgeProps["variant"];
  unselectedBadgeVariant?: BadgeProps["variant"];
  className?: string;
}

const defaultFormatIntegrationName = (
  integration: IntegrationSelectionOption
) => {
  if (integration?.name && typeof integration.name === "string") {
    return integration.name;
  }
  const id = integration?.id ?? "";
  return id.charAt(0).toUpperCase() + id.slice(1);
};

const IntegrationSelectionList: React.FC<IntegrationSelectionListProps> = ({
  integrations,
  selectedIntegrationIds,
  onToggle,
  loading = false,
  emptyMessage = (
    <div className="rounded-md border border-dashed border-border/60 bg-muted/20 p-6 text-sm text-muted-foreground">
      No integrations are currently available. Contact your administrator to set
      them up.
    </div>
  ),
  pendingIntegrationId = null,
  disabled = false,
  formatIntegrationName = defaultFormatIntegrationName,
  selectedBadgeLabel = "Enabled",
  unselectedBadgeLabel = "Disabled",
  selectedBadgeVariant = "secondary",
  unselectedBadgeVariant = "outline",
  className,
}) => {
  const selectedSet = React.useMemo(() => {
    if (!selectedIntegrationIds) {
      return new Set<string>();
    }
    if (
      typeof (selectedIntegrationIds as unknown as { has: unknown }).has ===
      "function"
    ) {
      return selectedIntegrationIds as Set<string>;
    }
    return new Set(Array.from(selectedIntegrationIds));
  }, [selectedIntegrationIds]);

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading integrations…
      </div>
    );
  }

  if (!loading && integrations.length === 0) {
    return <>{emptyMessage}</>;
  }

  return (
    <div className={cn("space-y-3", className)}>
      {integrations.map((integration) => {
        const isSelected = selectedSet.has(integration.id);
        const isPending = pendingIntegrationId === integration.id;
        const label = formatIntegrationName(integration);

        return (
          <label
            key={integration.id}
            className={cn(
              "flex items-start gap-4 rounded-lg border px-4 py-3 transition-colors",
              isSelected
                ? "border-primary/40 bg-primary/5"
                : "border-border bg-background hover:bg-muted/40",
              disabled ? "cursor-not-allowed opacity-75" : "cursor-pointer"
            )}
          >
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">{label}</span>
                <Badge
                  variant={isSelected ? selectedBadgeVariant : unselectedBadgeVariant}
                >
                  {isSelected ? selectedBadgeLabel : unselectedBadgeLabel}
                </Badge>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {integration.description ||
                  "Configure credentials, prompts, and connection details for this integration."}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isPending && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              <input
                type="checkbox"
                className="h-4 w-4 accent-primary"
                checked={isSelected}
                onChange={(event) => onToggle(integration.id, event.target.checked)}
                disabled={disabled || isPending}
              />
            </div>
          </label>
        );
      })}
    </div>
  );
};

export default IntegrationSelectionList;

